import React from 'react'
import { BarChart3, TrendingUp, Users, Heart, MessageCircle, Share2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { SocialProfile, SocialPost, Platform } from '@/types/social'

interface PlatformStatsProps {
  profiles: SocialProfile[]
  posts: SocialPost[]
}

const PLATFORM_COLORS: Record<Platform, string> = {
  INSTAGRAM: 'bg-gradient-to-r from-purple-500 to-pink-500',
  FACEBOOK: 'bg-blue-600',
  TWITTER: 'bg-sky-400',
  LINKEDIN: 'bg-blue-700',
  PINTEREST: 'bg-red-600',
  TIKTOK: 'bg-black'
}

export default function PlatformStats({ profiles, posts }: PlatformStatsProps) {
  const getStatsForPlatform = (platform: Platform) => {
    const profilesForPlatform = profiles.filter(p => p.platform === platform)
    const postsForPlatform = posts.filter(p => {
      const profile = profiles.find(prof => prof.id === p.profile_id)
      return profile?.platform === platform
    })

    const publishedPosts = postsForPlatform.filter(p => p.status === 'PUBLISHED')
    const scheduledPosts = postsForPlatform.filter(p => p.status === 'SCHEDULED')
    
    const totalFollowers = profilesForPlatform.reduce((sum, profile) => 
      sum + (profile.follower_count || 0), 0
    )

    const totalEngagement = publishedPosts.reduce((sum, post) => {
      const metrics = post.engagement_metrics
      if (!metrics) return sum
      return sum + (metrics.likes || 0) + (metrics.comments || 0) + (metrics.shares || 0)
    }, 0)

    const totalReach = publishedPosts.reduce((sum, post) => {
      return sum + (post.engagement_metrics?.reach || 0)
    }, 0)

    const avgEngagementRate = publishedPosts.length > 0
      ? publishedPosts.reduce((sum, post) => 
          sum + (post.engagement_metrics?.engagement_rate || 0), 0
        ) / publishedPosts.length
      : 0

    return {
      profileCount: profilesForPlatform.length,
      totalPosts: postsForPlatform.length,
      publishedPosts: publishedPosts.length,
      scheduledPosts: scheduledPosts.length,
      totalFollowers,
      totalEngagement,
      totalReach,
      avgEngagementRate
    }
  }

  const getOverallStats = () => {
    const totalProfiles = profiles.length
    const connectedProfiles = profiles.filter(p => p.connection_status === 'connected').length
    const totalPosts = posts.length
    const publishedPosts = posts.filter(p => p.status === 'PUBLISHED').length
    const scheduledPosts = posts.filter(p => p.status === 'SCHEDULED').length
    const draftPosts = posts.filter(p => p.status === 'DRAFT').length

    const totalFollowers = profiles.reduce((sum, profile) => 
      sum + (profile.follower_count || 0), 0
    )

    const totalEngagement = posts
      .filter(p => p.status === 'PUBLISHED')
      .reduce((sum, post) => {
        const metrics = post.engagement_metrics
        if (!metrics) return sum
        return sum + (metrics.likes || 0) + (metrics.comments || 0) + (metrics.shares || 0)
      }, 0)

    return {
      totalProfiles,
      connectedProfiles,
      totalPosts,
      publishedPosts,
      scheduledPosts,
      draftPosts,
      totalFollowers,
      totalEngagement
    }
  }

  const overallStats = getOverallStats()
  const activePlatforms = Array.from(new Set(profiles.map(p => p.platform)))

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* Overall Stats */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Reach</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {overallStats.totalFollowers.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground">
            {overallStats.connectedProfiles} of {overallStats.totalProfiles} profiles connected
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Posts</CardTitle>
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{overallStats.totalPosts}</div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="text-xs">
              {overallStats.publishedPosts} published
            </Badge>
            <Badge variant="secondary" className="text-xs">
              {overallStats.scheduledPosts} scheduled
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Engagement</CardTitle>
          <Heart className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {overallStats.totalEngagement.toLocaleString()}
          </div>
          <p className="text-xs text-muted-foreground">
            Total likes, comments, shares
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Platforms</CardTitle>
          <Share2 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{activePlatforms.length}</div>
          <div className="flex items-center gap-1 mt-2">
            {activePlatforms.slice(0, 4).map(platform => (
              <div
                key={platform}
                className={`w-3 h-3 rounded-full ${PLATFORM_COLORS[platform]}`}
                title={platform}
              />
            ))}
            {activePlatforms.length > 4 && (
              <span className="text-xs text-muted-foreground ml-1">
                +{activePlatforms.length - 4}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Platform-specific stats */}
      {activePlatforms.slice(0, 4).map(platform => {
        const stats = getStatsForPlatform(platform)
        
        return (
          <Card key={platform}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${PLATFORM_COLORS[platform]}`} />
                {platform}
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Followers:</span>
                  <span className="font-medium">{stats.totalFollowers.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Posts:</span>
                  <span className="font-medium">{stats.totalPosts}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Published:</span>
                  <span className="font-medium">{stats.publishedPosts}</span>
                </div>
                {stats.avgEngagementRate > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Avg. Engagement:</span>
                    <span className="font-medium">{stats.avgEngagementRate.toFixed(1)}%</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}